import { Metrics } from '@newrelic/browser-agent/features/metrics';
import { PageAction } from '@newrelic/browser-agent/features/page_action';
import { PageViewEvent } from '@newrelic/browser-agent/features/page_view_event';
import { PageViewTiming } from '@newrelic/browser-agent/features/page_view_timing';
import { MicroAgent } from '@newrelic/browser-agent/loaders/micro-agent';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
export class NewRelicManager {
    static _instance = new NewRelicManager();
    #microAgent;
    #logger;
    #initialized = false;
    #attributes;
    #version;
    constructor() {
        if (NewRelicManager._instance) {
            throw new Error(this.name +
                ' is a Singleton and can only be accessed through ' +
                this.name +
                '.getInstance()');
        }
        NewRelicManager._instance = this;
        this.#logger = LogService.create({ prefixes: ['SmartTag:', this.name + ':'] });
    }
    get name() {
        return 'NewRelicManager';
    }
    get utils() {
        return window?.PARA?.PlayerServices?.Utilities;
    }
    static getInstance() {
        return NewRelicManager._instance;
    }
    get attributes() {
        return this.#attributes;
    }
    set attributes(attributesObj) {
        this.#attributes = attributesObj;
    }
    #newRelicNamespaceExists = () => {
        return this.#initialized === true && !this.utils.Type.isNullOrUndefined(window?.newrelic);
    };
    #setCustomAttribute = (name, value) => {
        if (this.#newRelicNamespaceExists() && this.#attributes?.embedType === EmbedType.DESKTOP && this.utils.Type.isFunction(window.newrelic.setCustomAttribute) && !this.utils.Type.isNullOrUndefined(value)) {
            try {
                window?.newrelic?.setCustomAttribute(name, value);
            }
            catch (error) {
                this.#logger.warn('setCustomAttribute:', error);
            }
        }
    };
    addAttributes = () => {
        if (!this.utils.Type.isNullOrUndefined(this.attributes)) {
            for (const key in this.#attributes) {
                if (!this.utils.Type.isNullOrUndefined(this.#attributes[key])) {
                    this.#setCustomAttribute(key, this.#attributes[key]);
                }
            }
        }
    };
    addPageAction = (name, value) => {
        if (this.#newRelicNamespaceExists() && this.utils.Type.isFunction(this.#microAgent.addPageAction) && !this.utils.Type.isNullOrUndefined(value)) {
            try {
                this.#microAgent?.addPageAction(name, {
                    value: value,
                    assetType: this.attributes.assetType,
                    embedType: this.attributes.embedType,
                    userType: this.attributes.userType
                });
            }
            catch (error) {
                this.#logger.warn('addRelease:', error);
            }
        }
    };
    addRelease = () => {
        if (this.#newRelicNamespaceExists() && this.utils.Type.isFunction(this.#microAgent.addRelease && !this.utils.Type.isNullOrUndefined(this.#version))) {
            try {
                this.#microAgent?.addRelease('SmartTag', this.#version);
            }
            catch (error) {
                this.#logger.warn('addRelease:', error);
            }
        }
        if (this.#newRelicNamespaceExists() && this.#attributes?.embedType === EmbedType.DESKTOP && this.utils.Type.isFunction(window.newrelic.addRelease) && !this.utils.Type.isNullOrUndefined(this.#version)) {
            try {
                window?.newrelic?.addRelease('SmartTag', this.#version);
            }
            catch (error) {
                this.#logger.warn('addRelease:', error);
            }
        }
    };
    setApplicationVersion = () => {
        if (this.#newRelicNamespaceExists() && this.utils.Type.isFunction(this.#microAgent.setApplicationVersion) && !this.utils.Type.isNullOrUndefined(this.#version)) {
            try {
                this.#microAgent?.setApplicationVersion(this.#version);
            }
            catch (error) {
                this.#logger.warn('setApplicationVersion:', error);
            }
        }
        if (this.#newRelicNamespaceExists() && this.#attributes.embedType === EmbedType.DESKTOP && this.utils.Type.isFunction(window.newrelic.setApplicationVersion) && !this.utils.Type.isNullOrUndefined(this.#version)) {
            try {
                window?.newrelic?.setApplicationVersion(this.#version);
            }
            catch (error) {
                this.#logger.warn('setApplicationVersion:', error);
            }
        }
    };
    #initNewRelic = () => {
        const options = {
            info: {
                beacon: 'bam.nr-data.net',
                errorBeacon: 'bam.nr-data.net',
                licenseKey: 'NRJS-b5dcb3a7b0855a31fdd',
                applicationID: '1103285613',
                sa: 1
            },
            loader_config: {
                accountID: '2936348',
                trustKey: '2321606',
                agentID: '1103285613',
                licenseKey: 'NRJS-b5dcb3a7b0855a31fdd',
                applicationID: '1103285613'
            },
            init: {
                ajax: { deny_list: ['bam.nr-data.net'], enabled: false },
                distributed_tracing: { enabled: false },
                jserrors: { enabled: false },
                privacy: { cookies_enabled: false },
                session_replay: { enabled: false },
                session_trace: { enabled: false },
                spa: { enabled: false }
            }
        };
        return new Promise((resolve, reject) => {
            try {
                this.#logger.log('initialize', 'New Relic');
                this.#microAgent = new MicroAgent({
                    ...options,
                    features: [
                        Metrics,
                        PageAction,
                        PageViewEvent,
                        PageViewTiming
                    ]
                });
                this.utils.Delay.delay().then(() => {
                    resolve({ msg: 'New Relic agent initialized', success: true });
                });
            }
            catch (error) {
                reject({ msg: 'New Relic agent initialization failed:', success: false, result: error });
            }
        });
    };
    initialize = (runtimeVO = window?.SmartTag?.runtimeVO, version = window?.SmartTag?.info?.version) => {
        this.#version = version;
        return new Promise((resolve, reject) => {
            if (this.#initialized === true) {
                this.#logger.log('initialize', 'NewRelic is disabled or already initialized');
                resolve({ msg: 'NewRelic is disabled or already initialized', success: true, result: this });
            }
            else if (this.#initialized === false && this.utils.Convert.stringToBoolean(runtimeVO?.readyCallback.newRelic) === true) {
                this.#initialized = true;
                this.#initNewRelic().then((data) => {
                    resolve({ msg: 'NewRelic startup process', success: true, result: this });
                }).catch((error) => {
                    reject({ msg: 'NewRelic startup process failed:', sucess: false, result: error });
                });
            }
            else {
                resolve({ msg: 'NewRelic startup process skipped', success: true, result: this });
            }
        });
    };
}
